import { supabase } from '../supabase';
// Adicionando slug ao tipo Game
import { Game as BaseGame, UserGame } from './types'; // Renomear import para evitar conflito

// Função utilitária exportada para normalizar/criar slugs
export function normalizeSlug(text: string): string {
    if (!text) return '';
    return text
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remover acentos
        .replace(/[^a-z0-9\s-]/g, '') // Remover caracteres não alfanuméricos (exceto espaço e hífen)
        .replace(/\s+/g, '-') // Substituir espaços por hífens
        .replace(/-+/g, '-') // Remover hífens duplicados
        .replace(/^-+|-+$/g, '') // Remover hífens no início/fim
        .substring(0, 75); // Limitar tamanho
}

// Definir o tipo Game estendido com slug
export type Game = BaseGame & {
  slug?: string | null;
};

// Estender o tipo Game para incluir user_game e outros dados agregados
type GameWithUserData = Game & {
  user_game?: UserGame;
  stats?: any; // Manter tipo genérico por enquanto ou definir um tipo Stats
  store_products?: any[]; // Manter tipo genérico ou definir StoreProduct com Store
};

// Função para obter a URL completa de um jogo (usará o slug do objeto game)
export function getGameUrl(game: Game): string {
  // Assumindo que o objeto 'game' agora terá a propriedade 'slug' populada
  const slug = game.slug || ''; // Usar slug existente ou gerar se faltar?
                                // Melhor garantir que sempre exista no BD.
  return `/games/${slug}`;
}

// Função auxiliar simplificada: Mescla apenas user_game e store_products.
// Assume que gameData já contém o campo 'stats' com dados globais.
async function mergeAdditionalData(gameData: Game, userId?: string): Promise<GameWithUserData> {
    const gameWithUserData = { ...gameData } as GameWithUserData;
    const gameId = gameData.id;

    // Priorizar médias do campo stats, se existirem, sobre as da tabela games
    if (gameData.stats) {
        gameWithUserData.average_rating = gameData.stats.average_rating ?? gameData.average_rating;
        gameWithUserData.average_complexity = gameData.stats.average_complexity ?? gameData.average_complexity;
    } else {
        // Se game.stats não existir por algum motivo, inicialize-o.
        gameWithUserData.stats = {
            owned_count: 0, wishlist_count: 0, rating_count: 0, play_count: 0, played_count: 0,
            average_rating: gameData.average_rating,
            average_complexity: gameData.average_complexity
        };
        console.warn(`detailsService - Campo game.stats não encontrado para ${gameId}. Inicializando.`);
    }

    // 1. Se houver usuário, buscar dados específicos dele
    if (userId) {
      console.log(`detailsService - Buscando user_game para userId: ${userId}, gameId: ${gameId}`);
      try {
          const { data: userGameData, error: userGameError } = await supabase
            .from('user_games')
            .select('*')
            .eq('user_id', userId)
            .eq('game_id', gameId)
            .maybeSingle();

          if (!userGameError && userGameData) {
            console.log(`detailsService - user_game encontrado para ${userId}/${gameId}`);
            gameWithUserData.user_game = userGameData;
          } else if (userGameError) {
            console.warn(`detailsService - Erro ao buscar user_game para ${userId}/${gameId}:`, userGameError);
          }
      } catch(error) {
          console.error(`detailsService - Exceção ao buscar user_game para ${userId}/${gameId}:`, error);
      }
    }

    // 2. Buscar produtos em lojas
    console.log(`detailsService - Buscando store_products para gameId: ${gameId}`);
    try {
        const { data: storeProducts, error: storeError } = await supabase
          .from('store_products')
          .select(`*, store:stores(*)`) 
          .eq('game_id', gameId)
          .eq('available', true);

        if (!storeError && storeProducts) {
          gameWithUserData.store_products = storeProducts;
        }
         else if (storeError) {
            console.warn(`detailsService - Erro ao buscar store_products para ${gameId}:`, storeError);
        }
    } catch (error) {
        console.error(`detailsService - Exceção ao buscar store_products para ${gameId}:`, error);
    }

    console.log(`detailsService - Dados finais mesclados (sem RPC inicial de stats) para ${gameId}:`, gameWithUserData);
    return gameWithUserData;
}

// Função para verificar se uma string é um UUID válido
function isUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

// Função para buscar um jogo pelo slug
export async function getGameBySlug(slug: string, userId?: string): Promise<GameWithUserData> {
  try {
    console.log('Carregando jogo com slug:', slug, 'para usuário:', userId || 'anônimo');
    
    // Se o slug for um UUID, buscar por ID
    if (isUUID(slug)) {
      console.log('Slug é um UUID, buscando por ID:', slug);
      return await getGameById(slug, userId);
    }

    const normalizedSlug = normalizeSlug(slug);

    // Buscar o jogo INCLUINDO o campo 'stats' diretamente
    const { data: game, error } = await supabase
      .from('games')
      .select('*, stats')
      .eq('slug', normalizedSlug)
      .maybeSingle();

    if (error) {
        console.error(`Erro Supabase ao buscar slug ${normalizedSlug}:`, error);
        throw error;
    }
    if (!game) {
      throw new Error(`Nenhum jogo encontrado para o slug: ${slug} (normalizado: ${normalizedSlug})`);
    }
    console.log('Jogo encontrado pelo slug (com stats embutido?):', game.id, game.name, game.stats);

    // Chamar a função auxiliar para mesclar user_game e store_products
    return await mergeAdditionalData(game, userId);

  } catch (error) {
    console.error(`Erro ao buscar jogo com slug ${slug}:`, error);
    throw error;
  }
}

// Função para buscar um jogo pelo ID
export async function getGameById(id: string, userId?: string): Promise<GameWithUserData> {
  try {
    console.log('Carregando jogo com ID:', id, 'para usuário:', userId || 'anônimo');
    
    // Buscar o jogo INCLUINDO o campo 'stats' diretamente
    const { data: game, error } = await supabase
      .from('games')
      .select('*, stats') // <<< GARANTIR que stats seja selecionado
      .eq('id', id)
      .single();
    
    if (error) throw error;
    if (!game) {
      throw new Error(`Jogo com ID ${id} não encontrado`);
    }
    console.log('Jogo encontrado pelo ID (com stats embutido?):', game.id, game.name, game.stats);

    // Chamar a função auxiliar para mesclar user_game e store_products
    return await mergeAdditionalData(game, userId);

  } catch (error) {
    console.error(`Erro ao buscar jogo com ID ${id}:`, error);
    throw error;
  }
} 